import axios from 'axios';
import { mount } from '../dom-lifecycle';

const TIME_LIMIT_MS = 30 * 60 * 1000;
const UPDATE_INTERVAL_MS = 30 * 1000;
const RESUME_CARD_MS = 5 * 1000;
// Kept high: minStep already scales the noise filter with accuracy and the anchor survives a
// rejected fix, so a coarse reading only delays crediting. iPhones routinely report 65 m.
const ACC_THRESHOLD = 100;
const MIN_STEP_M = 3;
const MAX_SPEED_MS = 12;
const STALE_FIX_MS = 20 * 1000;
const RESTART_WATCH_MS = 60 * 1000;
const GEO_OPTIONS = { enableHighAccuracy: true, maximumAge: 0, timeout: 8000 };
// Start gate: same requirements as tracking, only more patient — a cold fix rarely lands in 8 s.
const GATE_GEO_OPTIONS = { ...GEO_OPTIONS, timeout: 20000 };

function haversine(a, b) {
    const R = 6371000, toRad = d => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

function pad(n) {
    return String(n).padStart(2, '0');
}

function clock(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
}

function hms(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

function km(meters) {
    return (meters / 1000).toFixed(2).replace('.', ',') + ' km';
}

function accuracyBucket(meters) {
    if (meters <= 10) return '0-10';
    if (meters <= 30) return '10-30';
    if (meters <= 50) return '30-50';
    if (meters <= 100) return '50-100';
    return '100+';
}

function isMobile() {
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
        (navigator.maxTouchPoints > 0 && matchMedia('(pointer: coarse)').matches);
}

function plural(n, one, few, many) {
    if (n === 1) return one;
    if (n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14)) return few;
    return many;
}

mount('[data-challenge]', function (el, refs) {
    const startUrl = el.dataset.startUrl;
    const updateUrl = el.dataset.updateUrl;
    const storageKey = `challenge:${updateUrl}`;
    const startDeadline = Date.parse(el.dataset.startDeadline || '');
    const GOAL_M = parseInt(el.dataset.goal, 10) || 1000;
    // Anchored on the server clock (filledAt) — a device clock that is off would end the session early.
    const serverNow = Date.parse(el.dataset.serverNow || '');
    const clockOffset = isNaN(serverNow) ? 0 : Date.now() - serverNow;

    let watchId = null, wakeLock = null, tickTimer = null, updateTimer = null, resumeTimer = null;
    let startTime = null, lastPos = null, distanceM = 0;
    let pendingResume = false, finished = false;
    let lastFixAt = null, lastGoodFixAt = null, hiddenAt = null;

    // Session diagnostics: tells apart "no fixes arrived" from "fixes arrived and were filtered".
    const stats = {
        fixes: 0, lowAccuracy: 0, belowStep: 0, tooFast: 0,
        resumes: 0, watchRestarts: 0, maxGapS: 0, hiddenS: 0,
        accuracy: {}, errors: {},
        clockSkewS: Math.round(clockOffset / 1000),
    };

    function now() {
        return Date.now() - clockOffset;
    }

    function showPage(name) {
        refs.all.page.forEach(page => (page.hidden = page.dataset.page !== name));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function showCard(name) {
        ['card-active', 'card-resumed', 'card-inactive'].forEach(ref => {
            const card = refs[ref];
            if (card) card.hidden = ref !== name;
        });
    }

    function persist() {
        localStorage.setItem(storageKey, JSON.stringify({ distanceM, lastPos, stats }));
    }

    function restore() {
        const server = parseFloat(el.dataset.distance || '0');
        let saved = {};
        try {
            saved = JSON.parse(localStorage.getItem(storageKey)) || {};
        } catch { /* corrupted entry — ignore */ }
        distanceM = Math.max(server, saved.distanceM || 0);
        lastPos = saved.lastPos || null;
        pendingResume = lastPos !== null;
        // Counters must survive a reload; the skew stays from the current render.
        Object.assign(stats, saved.stats, { clockSkewS: stats.clockSkewS });
    }

    function render() {
        refs.timeElapsed.textContent = clock(now() - startTime);
        refs.timeRemaining.textContent = hms(startTime + TIME_LIMIT_MS - now());
        refs.distance.textContent = km(distanceM);
        refs.distanceRemaining.textContent = km(Math.max(0, GOAL_M - distanceM));
        const percent = Math.min(100, Math.round((distanceM / GOAL_M) * 100));
        refs.progress.value = percent;
        refs.percent.textContent = `${percent}%`;
    }

    async function requestWakeLock() {
        try {
            if ('wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen');
        } catch { /* e.g. low battery — the challenge keeps running */ }
    }

    function beforeUnload(e) {
        e.preventDefault();
        e.returnValue = '';
    }

    function showResumedCard(step) {
        refs.distanceUpdate.textContent = km(step);
        showCard('card-resumed');
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(() => {
            resumeTimer = null;
            showCard('card-active');
        }, RESUME_CARD_MS);
    }

    function onPosition(pos) {
        const c = pos.coords;
        const bucket = accuracyBucket(c.accuracy);
        stats.fixes++;
        stats.accuracy[bucket] = (stats.accuracy[bucket] || 0) + 1;
        stats.maxGapS = Math.max(stats.maxGapS, Math.round((Date.now() - lastFixAt) / 1000));
        lastFixAt = Date.now();
        if (c.accuracy > ACC_THRESHOLD) {
            stats.lowAccuracy++;
            return;
        }
        lastGoodFixAt = Date.now();

        const fix = { lat: c.latitude, lng: c.longitude, t: pos.timestamp };
        let step = 0;
        if (!lastPos) {
            lastPos = fix;
        } else {
            step = haversine(lastPos, fix);
            const speed = fix.t > lastPos.t ? step / ((fix.t - lastPos.t) / 1000) : 0;
            // Próg szumu skalowany dokładnością odczytu: przy dobrym GPS-ie (5-10 m) łapiemy
            // krótkie odcinki, przy słabym wymagamy większego przesunięcia, żeby dryf pozycji
            // nie naliczał metrów na postoju.
            const minStep = Math.max(MIN_STEP_M, c.accuracy);
            // Najważniejsze: kotwica (lastPos) zostaje, dopóki odczyt nie przejdzie progu.
            // Wcześniej przesuwała się przy każdym odczycie, także odrzuconym — a przy
            // watchPosition ~1 Hz i tempie spacerowym pojedynczy odcinek to ok. 1,4 m, czyli
            // poniżej progu. Każdy taki fragment przepadał bezpowrotnie i na realnym 1 km
            // licznik dochodził do ok. 185 m (symulacja) — dokładnie to, co zgłosili userzy.
            if (pendingResume || (step >= minStep && speed <= MAX_SPEED_MS)) {
                distanceM += step;
                lastPos = fix;
            } else if (step < minStep) {
                stats.belowStep++;
            } else {
                stats.tooFast++;
            }
        }
        if (pendingResume) showResumedCard(step);
        else if (!resumeTimer) showCard('card-active');
        pendingResume = false;
        persist();
        render();
        if (distanceM >= GOAL_M) sendUpdate();
    }

    function onPositionError(err) {
        stats.errors[err.code] = (stats.errors[err.code] || 0) + 1;
        clearTimeout(resumeTimer);
        resumeTimer = null;
        showCard('card-inactive');
        if (err.code === 1 && !startTime) showPage('gps-warning');
    }

    function patchProgress() {
        return axios.patch(updateUrl, new URLSearchParams({
            distance: distanceM.toFixed(3),
            telemetry: JSON.stringify(stats),
        }));
    }

    async function sendUpdate() {
        if (finished) return;
        try {
            const { data } = await patchProgress();
            if (data.result == 'completed') complete();
            else if (data.result == 'timeout') showTimeout();
        } catch { /* transient network error — keep measuring */ }
    }

    function startWatch() {
        lastFixAt = lastGoodFixAt = Date.now();
        watchId = navigator.geolocation.watchPosition(onPosition, onPositionError, GEO_OPTIONS);
    }

    // watchPosition can go silent with no error (page suspended, permission revoked mid-session),
    // leaving the badge green and the counter at 0,00 km.
    function checkFixFreshness() {
        if (Date.now() - lastGoodFixAt >= STALE_FIX_MS && !resumeTimer) showCard('card-inactive');
        if (Date.now() - lastFixAt >= RESTART_WATCH_MS) {
            navigator.geolocation.clearWatch(watchId);
            stats.watchRestarts++;
            startWatch();
        }
    }

    function tick() {
        render();
        checkFixFreshness();
        if (!finished && now() - startTime >= TIME_LIMIT_MS) timedOut();
    }

    async function begin() {
        startTime ??= now();
        clearInterval(countdownTimer);
        showPage('in-progress');
        showCard('card-active');
        render();
        window.addEventListener('beforeunload', beforeUnload);
        await requestWakeLock();
        tickTimer = setInterval(tick, 250);
        updateTimer = setInterval(sendUpdate, UPDATE_INTERVAL_MS);
        startWatch();
    }

    function stop() {
        if (watchId !== null) navigator.geolocation.clearWatch(watchId);
        clearInterval(tickTimer);
        clearInterval(updateTimer);
        clearTimeout(resumeTimer);
        wakeLock?.release().catch(() => {});
        watchId = wakeLock = null;
        window.removeEventListener('beforeunload', beforeUnload);
    }

    function complete() {
        finished = true;
        const elapsed = now() - startTime;
        stop();
        localStorage.removeItem(storageKey);
        const minutes = Math.floor(elapsed / 60000);
        const seconds = Math.round((elapsed % 60000) / 1000);
        refs.resultMinutes.textContent = minutes;
        refs.resultMinutesLabel.textContent = plural(minutes, 'minuta', 'minuty', 'minut');
        refs.resultSeconds.textContent = seconds;
        refs.resultSecondsLabel.textContent = plural(seconds, 'sekunda', 'sekundy', 'sekund');
        showPage('completed');
    }

    function showTimeout() {
        if (finished) return;
        finished = true;
        stop();
        localStorage.removeItem(storageKey);
        showPage('timeout');
    }

    function timedOut() {
        if (finished) return;
        // Delay the finalizing request ~3s so the last position report has a chance to
        // land — the backend still accepts a completed distance shortly after the limit.
        setTimeout(() => patchProgress().catch(() => {}), 3000);
        showTimeout();
    }

    async function startChallenge(event) {
        const button = event?.currentTarget ?? refs.btnStart;
        const label = button.textContent;
        const release = () => {
            button.disabled = false;
            button.textContent = label;
        };
        button.disabled = true;
        button.textContent = 'Szukamy Twojej lokalizacji…';

        let position = null;
        try {
            position = await new Promise((resolve, reject) =>
                navigator.geolocation.getCurrentPosition(resolve, reject, GATE_GEO_OPTIONS));
        } catch { /* no fix — handled below, same as a fix too coarse to measure with */ }
        // Without a start anchor within the threshold the measurement cannot work, and the 30 min
        // limit would already be running.
        if (!position || position.coords.accuracy > ACC_THRESHOLD) {
            showPage('gps-warning');
            release();
            return;
        }
        try {
            // A repeated start returns the same filledAt, so a retry never restarts the count.
            const { data } = await axios.post(startUrl);
            startTime = Date.parse(data.filledAt) || now();
        } catch {
            // Start window closed on the backend → show the expired view; any
            // other (transient) failure → let the user try again.
            if (!isNaN(startDeadline) && now() >= startDeadline) showTimeout();
            else release();
            return;
        }
        begin();
    }

    function startCountdown() {
        if (isNaN(startDeadline)) return null;
        let timer = null;
        const update = () => {
            const left = startDeadline - now();
            if (left <= 0) {
                clearInterval(timer);
                showTimeout();
                return false;
            }
            const total = Math.floor(left / 1000);
            const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
            refs.hours.textContent = pad(h);
            refs.hoursLabel.textContent = plural(h, 'godzina', 'godziny', 'godzin');
            refs.minutes.textContent = pad(m);
            refs.minutesLabel.textContent = plural(m, 'minuta', 'minuty', 'minut');
            refs.seconds.textContent = pad(s);
            refs.secondsLabel.textContent = plural(s, 'sekunda', 'sekundy', 'sekund');
            return true;
        };
        if (!update()) return null;
        timer = setInterval(update, 1000);
        return timer;
    }

    const onVisibility = async () => {
        if (watchId === null) return;
        if (document.visibilityState == 'visible') {
            if (hiddenAt) {
                stats.hiddenS += Math.round((Date.now() - hiddenAt) / 1000);
                hiddenAt = null;
            }
            if (wakeLock === null) await requestWakeLock();
            pendingResume = true;
            stats.resumes++;
        } else {
            hiddenAt = Date.now();
        }
    };
    document.addEventListener('visibilitychange', onVisibility);

    refs.btnStart.addEventListener('click', startChallenge);
    refs.btnGpsRetry.addEventListener('click', startChallenge);

    let countdownTimer = null;
    if (el.dataset.state == 'accepted') {
        const filled = Date.parse(el.dataset.filledAt);
        const done = Date.parse(el.dataset.finishedAt);
        startTime = filled;
        const elapsed = (done - filled) || 0;
        refs.resultMinutes.textContent = Math.floor(elapsed / 60000);
        refs.resultSeconds.textContent = Math.round((elapsed % 60000) / 1000);
        showPage('completed');
    } else if (el.dataset.state == 'expired') {
        showPage('timeout');
    } else if (!isMobile()) {
        showPage('only-mobile');
    } else if (el.dataset.filledAt) {
        startTime = Date.parse(el.dataset.filledAt);
        restore();
        if (now() - startTime >= TIME_LIMIT_MS) timedOut();
        else begin();
    } else {
        showPage('intro');
        countdownTimer = startCountdown();
    }

    return {
        unmount() {
            stop();
            clearInterval(countdownTimer);
            document.removeEventListener('visibilitychange', onVisibility);
        },
    };
});
